'use strict';
import classNames from 'classnames';
import get from 'lodash/get';
import isDate from 'lodash/isDate';
import isNumber from 'lodash/isNumber';
import memoize from 'lodash/memoize';
import moment from 'moment/moment';
import * as T from 'prop-types';
import React, {Component} from 'react';
import {CommentIndicator} from '../../../comments/components/CommentIndicator';
import {Button} from '../../../components/buttons/Button';
import {StatefulButton} from '../../../components/buttons/StatefulButton';
import {ListItem} from '../../../components/lists/ListItem';
import {SvgIcon} from '../../../core-components/svg-menu-icons/SvgIcon';
import {getCollection, getId} from '../../../core-utilities/domain/ReduxUtils';
import {ButtonState as ButtonMode} from '../../../core-utilities/enums/ButtonState';
import {getMessagesFromProps} from '../../../core-utilities/react-i18n';
import {truncateName} from '../../../core-utilities/upload-service/FileUtilities';
import {Image} from '../../../files/components/Image';
import {fileIsImage} from '../../../files/domain/FileDomain';


const cachedFmtDate = memoize(fmtDate);

export class FileFulfillmentItemView extends Component {
  // TODO: create separate render path if rendering an upload that is in progress. figure that out
  render() {
    const props = this.props;
    const model = props.model;
    const currentFileAsset = model.currentFileAsset;
    const file = get(currentFileAsset, 'file');
    const isRejected = currentFileAsset.status === 'rejected';
    const isApproved = currentFileAsset.status === 'approved';
    const isArchived = currentFileAsset.status === 'archived' || currentFileAsset.isArchived;
    const canApprove = !isArchived && currentFileAsset.validStatuses.includes('approved');
    const canReject = !isArchived && currentFileAsset.validStatuses.includes('rejected');
    const showStatusButtons = isRejected || isApproved || canApprove || canReject;

    return !file ? null : (
      <div
        className={classNames("FileFulfillmentItem", props.className, {
          'FileFulfillmentItem_isArchived': currentFileAsset.isArchived,
        })}
      >
        <div className="FileFulfillmentItem__header">
          <CommentIndicator
            className="FileFulfillmentItem__chat"
            displayTitle={false}
            userId={props.userId}
            chatId={getId(currentFileAsset)}
            collection={getCollection(currentFileAsset)}
            allowNav={false}

            init={props.init}
            {...getMessagesFromProps(props, CommentIndicator.messageProps)}
          />
          <div className="FileFulfillmentItem__info">
            <span>
              {currentFileAsset.status}
            </span>
          </div>
          <Button
            className="FileFulfillmentItem__options"
            iconClass="context-icon"
            bindParam={model}
            onClick={props.onContextMenu}
          />
        </div>

        <ListItem
          className="FileFulfillmentItem__content"
          bindParam={model}
          onClick={props.onClick}
          onContextMenu={props.onContextMenu}
        >
          <div className="FileFulfillmentItem__imageContainer">
            {props.isOpening ? (
              <div className={classNames("FileFulfillmentItem__opening")}>
                <i className="ion-load-c spin" />
              </div>
            ) : null}

            {fileIsImage(file) ? (
              <Image
                className="FileFulfillmentItem__image"
                title={props.name}
                url={file.url}
                displayWidth={props.displayWidth}
                displayHeight={props.displayHeight}
                crop={true}
                targetWidth={props.targetWidth}
                targetHeight={props.targetHeight}
              />
            ) : (
              <SvgIcon
                className="FileFulfillmentItem__fileIcon"
                isFile={true}
                filename={file.name}
              />
            )}
          </div>
        </ListItem>

        {timestamp(props, currentFileAsset)}

        <div
          className={classNames("FileFulfillmentItem__statusButtons", {
            'hide-elem': !showStatusButtons,
          })}
        >
          <StatefulButton
            className="FileFulfillmentItem__statusButton"
            startClassName={classNames("FileFulfillmentItem__rejectButton_start", {
              FileFulfillmentItem__rejectButton_active: isRejected,
            })}
            confirmClassName={isRejected ? 'FileFulfillmentItem__rejectButton_active_confirm' : void 0}
            useConfirmation={true}
            usePropState={true}
            mode={props.rejectMode}
            disabled={!canReject || props.approveMode !== ButtonMode.START}
            startLabel={isRejected ? props.rejectActiveLabel : props.rejectStartLabel}
            confirmLabel={isRejected ? props.rejectRevertLabel : void 0}
            successLabel={props.rejectSuccessLabel}
            onClick={props.onSelectRejected}
          />
          <StatefulButton
            className="FileFulfillmentItem__statusButton"
            startClassName={classNames("FileFulfillmentItem__approveButton_start", {
              FileFulfillmentItem__approveButton_active: isApproved,
            })}
            confirmClassName={isApproved ? 'FileFulfillmentItem__approveButton_active_confirm' : void 0}
            useConfirmation={true}
            usePropState={true}
            mode={props.approveMode}
            disabled={!canApprove || props.rejectMode !== ButtonMode.START}
            startLabel={isApproved ? props.approveActiveLabel : props.approveStartLabel}
            confirmLabel={isApproved ? props.approveRevertLabel : void 0}
            successLabel={props.approveSuccessLabel}
            onClick={props.onSelectApproved}
          />
        </div>
      </div>
    );
  }
}

function timestamp(props, currentFileAsset) {
  return (
    <div className="timestamp">
      {`${truncateName(props.name, 12)} ${props.model.ord} \u2014 ${cachedFmtDate(currentFileAsset.dateCreated)}`}
    </div>
  );
}

FileFulfillmentItemView.propTypes = {
  className: T.string,
  isCardMode: T.bool,

  model: T.object,
  name: T.string,
  isOpening: T.bool,

  onClick: T.func,
  onContextMenu: T.func,
  onSelectRejected: T.func,
  onSelectApproved: T.func,

  rejectActiveLabel: T.string,
  rejectStartLabel: T.string,
  rejectSuccessLabel: T.string,
  rejectRevertLabel: T.string,
  approveActiveLabel: T.string,
  approveStartLabel: T.string,
  approveSuccessLabel: T.string,
  approveRevertLabel: T.string,
};

FileFulfillmentItemView.defaultProps = {
  className: '',
  isCardMode: true,
  targetWidth: 320,
  targetHeight: 320,
};

FileFulfillmentItemView.messages = [
  {code: 'requirement.reject.active.label', prop: 'rejectActiveLabel'},
  {code: 'requirement.reject.start.label', prop: 'rejectStartLabel'},
  {code: 'requirement.reject.success.label', prop: 'rejectSuccessLabel'},
  {code: 'requirement.reject.revert.label', prop: 'rejectRevertLabel'},
  {code: 'requirement.approve.active.label', prop: 'approveActiveLabel'},
  {code: 'requirement.approve.start.label', prop: 'approveStartLabel'},
  {code: 'requirement.approve.success.label', prop: 'approveSuccessLabel'},
  {code: 'requirement.approve.revert.label', prop: 'approveRevertLabel'},
  ...CommentIndicator.messages,
];

FileFulfillmentItemView.messageProps = [
  'rejectActiveLabel',
  'rejectStartLabel',
  'rejectSuccessLabel',
  'rejectRevertLabel',
  'approveActiveLabel',
  'approveStartLabel',
  'approveSuccessLabel',
  'approveRevertLabel',
  ...CommentIndicator.messageProps,
];

function fmtDate(date) {
  return isDate(date) || isNumber(date)
    ? moment(date).format('l LT')
    : null;
}



// WEBPACK FOOTER //
// ./src/js/requirements/components/FileFulfillmentItem/FileFulfillmentItemView.js